import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { LocalStorageService } from 'ngx-localstorage';
import { Observable, catchError, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { User } from './user.model';
import { showErrorDialog } from '../utils/utils';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { STORE_PROFILE_PICTURES } from '../types/constants';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private http = inject(HttpClient);
  private logger = inject(NGXLogger);
  private storage = inject(LocalStorageService);

  private apiEndpoint = `${environment.apiEndpoint}/api/users`;
  private dbService = inject(NgxIndexedDBService);

  private readonly TTL_MS = 30 * 24 * 60 * 60 * 1000;

  public getUser$(email: string): Observable<User> {
    const user = this.storage.get<User>(`user_${email}`);

    return user
      ? of(user)
      : this.http.get<User>(`${this.apiEndpoint}/${email}`).pipe(
          tap((res) => this.storage.set(`user_${email}`, res)),
          catchError((error) => {
            this.logger.error(error);
            showErrorDialog(error);

            return throwError(() => error);
          })
        );
  }

  public getProfilePicture$(email: string): Observable<string> {
    const now = Date.now();
    return from(this.dbService.getByKey<ProfilePictureRecord>(STORE_PROFILE_PICTURES, email)).pipe(
      switchMap((record) => {
        // If cache hit and not expired, return it
        if (record && record.expiresAt > now && typeof record.picture === 'string') {
          return of(record.picture);
        }

        // Otherwise, fetch from API
        return this.http
          .get(`${this.apiEndpoint}/profile-picture/${email}`, {
            responseType: 'text'
          })
          .pipe(
            switchMap((pictureBase64: string) => {
              const payload: ProfilePictureRecord = {
                id: email,
                picture: pictureBase64,
                updatedAt: now,
                expiresAt: now + this.TTL_MS
              };

              // Try add; if key exists, update (upsert)
              return from(this.dbService.add(STORE_PROFILE_PICTURES, payload)).pipe(
                catchError(() => from(this.dbService.update(STORE_PROFILE_PICTURES, payload))),
                map(() => pictureBase64)
              );
            }),
            // If network fails, but we had a stale cached value, return it as last resort
            catchError((err) => {
              if (record?.picture) return of(record.picture);
              throw err;
            })
          );
      })
    );
  }
}

export interface ProfilePictureRecord {
  id: string;
  picture: string;
  updatedAt: number;
  expiresAt: number;
}
